import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';

import {
    StyledCard,
    StyledCardContent,
    Text,
    ContainedButton,
    OutlinedButton,
    CalendarIcon,
} from '../../index';

import { StyledDialog, StyledEditSessionPaper } from './style';

import CreateSessionCard from '../Appointment/CreateSessionCard';

const Overlap = '/resources/images/overlap.svg';

import { GET_MEETS_BY_DATE, editOne, editGroup, editOpen } from '../../../services/provider';
import { alertError, alertSuccess } from '../../../redux/stateActions';

import socket from '../../../socket';

dayjs.extend(objectSupport);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const EditSessionDialog = ({ data, open, onClose, disabledTime, disabledDate, refetch }) => {
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [appointmentStartTime, setAppointmentStartTime] = useState('');
    const [appointmentEndTime, setAppointmentEndTime] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [meetsData, setMeetsData] = useState([]);
    const [overlapData, setOverlapData] = useState({});

    const reset = () => {
        if (!_.isEmpty(data)) {
            setAppointmentDate(dayjs(data.startDate));
            setAppointmentStartTime(
                `${data.startTime.split(':')[0]}:${data.startTime.split(':')[1]}`
            );
            setAppointmentEndTime(`${data.endTime.split(':')[0]}:${data.endTime.split(':')[1]}`);
            setName(data.name);
            setDescription(data.description);
        }
    };

    useEffect(() => {
        reset();
    }, [data]);

    /*--------------- Get Meets By date ---------------*/
    const {
        data: getMeetsData,
        error: getMeetsError,
        refetch: getMeetsRefetch,
    } = useQuery(GET_MEETS_BY_DATE, {
        variables: {
            date: !_.isNull(appointmentDate) && dayjs(appointmentDate).format('YYYY-MM-DD'),
            staff: user.staff.id,
        },
        skip: _.isNull(appointmentDate) || _.isEmpty(user),
    });

    useEffect(() => {
        if (!_.isNull(appointmentDate)) {
            getMeetsRefetch();
        }
    }, [appointmentDate]);

    useEffect(() => {
        if (getMeetsError) {
            dispatch(alertError(`Cannot get Session data: ${getMeetsError.message}`));
        }

        if (getMeetsData) {
            setMeetsData(getMeetsData.meets);
        }
    }, [getMeetsError, getMeetsData]);

    const handleDateChange = (date) => {
        setAppointmentDate(date);
        setErrors(errors.filter((error) => error.property !== 'appointmentDate'));
    };

    const handleStartTimeChange = (time) => {
        setAppointmentStartTime(time);
        setErrors(
            errors.filter(
                (error) =>
                    error.property !== 'appointmentStartTime' &&
                    error.property !== 'appointmentEndTime'
            )
        );
    };
    const handleEndTimeChabge = (time) => {
        setAppointmentEndTime(time);
        setErrors(
            errors.filter(
                (error) =>
                    error.property !== 'appointmentStartTime' &&
                    error.property !== 'appointmentEndTime'
            )
        );
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
        setErrors(errors.filter((error) => error.property !== 'name'));
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value);
        setErrors(errors.filter((error) => error.property !== 'description'));
    };

    const onCancel = () => {
        reset();
        setOverlapData({});
        onClose();
    };

    const handleSubmit = async () => {
        setOverlapData({});
        onClose();

        const newData = {
            id: data.id,
            name: name.trim(),
            description: description.trim(),
            date: dayjs(appointmentDate).format('YYYY-MM-DD'),
            startTime: dayjs()
                .set('date', dayjs(appointmentDate).get('date'))
                .set('hour', appointmentStartTime.split(':')[0])
                .set('minute', appointmentStartTime.split(':')[1])
                .format('HH:mm:ss.SSS'),
            endTime: dayjs()
                .set('date', dayjs(appointmentDate).get('date'))
                .set('hour', appointmentEndTime.split(':')[0])
                .set('minute', appointmentEndTime.split(':')[1])
                .format('HH:mm:ss.SSS'),
        };

        try {
            setLoading(true);

            let res;

            if (data.type === 'one') {
                res = await editOne(newData);
            }
            if (data.type === 'group') {
                res = await editGroup(newData);
            }
            if (data.type === 'open') {
                res = await editOpen(newData);
            }

            if (res.status === 200) {
                dispatch(alertSuccess(`Session updated`));
                getMeetsRefetch();
                refetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot update session: ${error.message}`));
        }

        setLoading(false);
    };

    const onSave = () => {
        const newErrors = [];

        if (name === '') {
            newErrors.push({
                property: 'name',
                message: 'Please provide a valid Name.',
            });
        }

        if (description === '') {
            newErrors.push({
                property: 'description',
                message: 'Please provide a valid Description.',
            });
        }

        if (appointmentDate === null) {
            newErrors.push({
                property: 'appointmentDate',
                message: 'Please provide a valid Date.',
            });
        }

        if (appointmentStartTime === '') {
            newErrors.push({
                property: 'appointmentStartTime',
                message: 'Please provide a valid Start Time.',
            });
        }

        if (appointmentEndTime === '') {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'Please provide a valid End Time.',
            });
        }

        if (
            dayjs()
                .set({
                    hour: appointmentStartTime.split(':')[0],
                    minute: appointmentStartTime.split(':')[1],
                })
                .isSameOrAfter(
                    dayjs().set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    }),
                    'minute'
                )
        ) {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'End Time must be after Start Time.',
            });
        }

        if (
            dayjs(appointmentDate)
                .set({
                    hour: appointmentStartTime.split(':')[0],
                    minute: appointmentStartTime.split(':')[1],
                })
                .isSameOrBefore(dayjs()) ||
            dayjs(appointmentDate)
                .set({
                    hour: appointmentEndTime.split(':')[0],
                    minute: appointmentEndTime.split(':')[1],
                })
                .isSameOrBefore(dayjs())
        ) {
            newErrors.push({
                property: 'appointmentEndTime',
                message: 'Session must be in the future.',
            });
        }

        setErrors(newErrors);
        if (!_.isEmpty(newErrors)) return;

        if (data.type !== 'one') {
            // check meet time overlap
            const otherMeets = meetsData.filter((meet) => meet.id !== data.id);

            const startTimeOverlap = otherMeets.find((meet) =>
                dayjs()
                    .set({
                        hour: appointmentStartTime.split(':')[0],
                        minute: appointmentStartTime.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: meet.startTime.split(':')[0],
                            minute: meet.startTime.split(':')[1],
                        }),
                        dayjs().set({
                            hour: meet.endTime.split(':')[0],
                            minute: meet.endTime.split(':')[1],
                        }),
                        'hour',
                        '()'
                    )
            );
            const endTimeOverlap = otherMeets.find((meet) =>
                dayjs()
                    .set({
                        hour: appointmentEndTime.split(':')[0],
                        minute: appointmentEndTime.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: meet.startTime.split(':')[0],
                            minute: meet.startTime.split(':')[1],
                        }),
                        dayjs().set({
                            hour: meet.endTime.split(':')[0],
                            minute: meet.endTime.split(':')[1],
                        }),
                        'hour',
                        '()'
                    )
            );
            if (startTimeOverlap) {
                setOverlapData(startTimeOverlap);
                return;
            }
            if (endTimeOverlap) {
                setOverlapData(endTimeOverlap);
                return;
            }
        }

        handleSubmit();
    };

    const onCancelConfirm = () => {
        setOverlapData({});
    };

    useEffect(() => {
        socket.on('newSession', () => {
            getMeetsRefetch();
        });
    }, []);

    return (
        <StyledDialog
            scroll="body"
            open={open}
            onClose={onClose}
            aria-labelledby="EditSessionDialog"
            aria-describedby="Edit session details"
            PaperComponent={StyledEditSessionPaper}
        >
            <CreateSessionCard
                errors={errors}
                loading={loading}
                appointmentDate={appointmentDate}
                handleDateChange={handleDateChange}
                appointmentStartTime={appointmentStartTime}
                handleStartTimeChange={handleStartTimeChange}
                appointmentEndTime={appointmentEndTime}
                handleEndTimeChabge={handleEndTimeChabge}
                name={name}
                handleNameChange={handleNameChange}
                description={description}
                handleDescriptionChange={handleDescriptionChange}
                onCancel={onCancel}
                onSave={onSave}
                disabledTime={disabledTime}
                disabledDate={disabledDate}
            />
            {!_.isEmpty(overlapData) && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(251, 251, 251, 0.9)',
                    }}
                >
                    <StyledCard $width="400px">
                        <StyledCardContent
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                rowGap: '10px',
                            }}
                        >
                            <Text $fontSize="16px" $bold style={{ alignSelf: 'center' }}>
                                Opps! Session Time Overlap
                            </Text>
                            <img
                                src={Overlap}
                                style={{
                                    alignSelf: 'center',
                                    marginTop: '10px',
                                    marginBottom: '10px',
                                }}
                            />
                            <Text $fontSize="14px" $bold>
                                Session 1 - {overlapData?.name}
                            </Text>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <CalendarIcon fill="pink" />
                                <Text
                                    $fontSize="12px"
                                    $color="pink"
                                    style={{ marginLeft: '5px' }}
                                    $bold
                                >
                                    {!_.isEmpty(overlapData) &&
                                        `${
                                            dayjs(overlapData.startDate).isSame(dayjs(), 'day')
                                                ? 'Today'
                                                : dayjs(overlapData.startDate).format('DD MMM YYYY')
                                        }, ${dayjs()
                                            .set({
                                                hour: overlapData.startTime.split(':')[0],
                                                minute: overlapData.startTime.split(':')[1],
                                            })
                                            .format('HH:mm')} - ${dayjs()
                                            .set({
                                                hour: overlapData.endTime.split(':')[0],
                                                minute: overlapData.endTime.split(':')[1],
                                            })
                                            .format('HH:mm')}`}
                                </Text>
                            </div>
                            <Text $fontSize="12px" $color="gray2">
                                {overlapData?.description}
                            </Text>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    columnGap: '10px',
                                    width: '100%',
                                    marginTop: '10px',
                                }}
                            >
                                <OutlinedButton onClick={onCancelConfirm} $width="80px">
                                    Cancel
                                </OutlinedButton>
                                <ContainedButton onClick={handleSubmit} $width="90px">
                                    Confirm
                                </ContainedButton>
                            </div>
                        </StyledCardContent>
                    </StyledCard>
                </div>
            )}
        </StyledDialog>
    );
};

EditSessionDialog.propTypes = {
    data: PropTypes.object,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    disabledTime: PropTypes.bool,
    disabledDate: PropTypes.bool,
    refetch: PropTypes.func.isRequired,
};

export default EditSessionDialog;
